<!-- Bu route layoutless: App.vue çıplak <router-view> basar, sarmalayıcı yoktur.
     Kendi sayfa düzenini kendisi kurar. -->
<template>
  <div class="sf-page">
    <div class="sf-sheet">
      <!-- Onam metni. Önceden height:500px + overflow:scroll ile kutuya hapsedilmişti:
           büyük ekranda sayfanın içinde ikinci bir kaydırma alanı oluşuyor, metin
           1900px genişlikte akıyordu. Artık tek sayfa kaydırması var ve satır uzunluğu
           .sf-document ile okunabilir bantta tutuluyor. -->
      <div class="sf-document">
        <sanitized-html :value-to-be-sanitized="documentBeforeClauses"></sanitized-html>
      </div>

      <!-- Madde bazlı Evet/Hayır. Belgedeki statik tablonun TAM YERİNE geçer (bkz.
           documentBeforeClauses/documentAfterClauses): aynı maddeler ekranda iki kez
           görünmesin. Uzaktan imza akışı da böyle çalışıyor
           (Stella.DigitalConsent.App/ClauseItem.vue).
           Legacy HTML şablonlarda yapısal madde olmadığı için burası hiç görünmez;
           belge bütün hâliyle basılır ve aşağıdaki eski "tümüne evet/hayır" devreye girer. -->
      <div v-if="clauses.length" class="sf-clauses">
        <p class="sf-clauses__intro">{{ $t("ConsentFormClausesHeaderText") }}</p>

        <div v-for="clause in clauses"
             :key="clause.clauseId"
             class="sf-clause"
             :class="{ 'sf-clause--blocked': isBlockingAnswer(clause) }">
          <div class="sf-clause__row">
            <div class="sf-clause__text">{{ clause.text }}</div>
            <div class="sf-clause__buttons">
              <button type="button"
                      class="btn btn-sm sf-clause__btn sf-clause__btn--no"
                      :class="{ 'active': answers[clause.clauseId] === false }"
                      :disabled="isSigned"
                      @click="setAnswer(clause, false)">
                <i class="fa fa-xmark"></i> {{ $t("No") }}
              </button>
              <button type="button"
                      class="btn btn-sm sf-clause__btn sf-clause__btn--yes"
                      :class="{ 'active': answers[clause.clauseId] === true }"
                      :disabled="isSigned"
                      @click="setAnswer(clause, true)">
                <i class="fa fa-check"></i> {{ $t("Yes") }}
              </button>
            </div>
          </div>

          <!-- Madde tanımında istenen serbest metin açıklaması. Yalnız cevap o yönü
               tetiklediğinde açılır ve doldurulmadan onam imzalanamaz (sunucu da
               reddeder: CustomerConsentFormAnswerLogic.Validate). -->
          <div v-if="requiresDetail(clause)" class="sf-clause__detail">
            <label class="sf-clause__detail-label" :for="'clauseDetail_' + clause.clauseId">
              {{ clause.detailLabel || $t("ConsentFormAnswerDetailLabel") }}
              <span class="sf-clause__detail-required">*</span>
            </label>
            <textarea :id="'clauseDetail_' + clause.clauseId"
                      class="form-control"
                      rows="2"
                      maxlength="2000"
                      :disabled="isSigned"
                      v-model="details[clause.clauseId]"></textarea>
          </div>
        </div>

        <div v-if="blockedClause" class="alert alert-danger sf-clauses__blocker">
          <i class="fa fa-triangle-exclamation"></i>
          {{ blockerMessage || $t("ConsentFormBlockerTriggered") }}
        </div>
        <div v-else-if="unansweredCount" class="alert alert-warning sf-clauses__pending">
          <i class="fa fa-circle-info"></i>
          {{ $t("ConsentFormAnswersIncomplete") }}
        </div>
        <div v-else-if="missingDetailCount" class="alert alert-warning sf-clauses__pending">
          <i class="fa fa-circle-info"></i>
          {{ $t("ConsentFormAnswerDetailRequired") }}
        </div>
      </div>

      <div v-if="documentAfterClauses" class="sf-document">
        <sanitized-html :value-to-be-sanitized="documentAfterClauses"></sanitized-html>
      </div>

      <!-- Eski form seviyesi onay. Yalnız yapısal maddesi olmayan (legacy) formlarda. -->
      <div v-if="!clauses.length && (clauseConsentType == 1 || clauseConsentType == -1)"
           class="sf-consent-row">
        <label class="sf-consent-row__label">
          <input type="checkbox"
                 v-model="isClausesConsented"
                 class="sf-consent-row__box"/>
          <span>{{ clauseConsentType == 1 ? $t("YesToAll") : $t("NoToAll") }}</span>
        </label>
      </div>

      <div
          v-if="formSettings.showClarificationText && (formSettings.clarificationText || formSettings.clarificationTextLink)"
          class="sf-consent-row">
        <label class="sf-consent-row__label" for="ClarificationText">
          <input id="ClarificationText"
                 type="checkbox"
                 v-model="clarificationConsented"
                 class="sf-consent-row__box"/>
          <span>
            {{ formSettings.clarificationText }}
            <a v-if="formSettings.clarificationTextLink"
               :href="formSettings.clarificationTextLink"
               target="_blank"
               class="sf-consent-row__link">
              {{ $t("ClarificationText") }}
            </a>
          </span>
        </label>
      </div>

      <div
          v-if="formSettings.showExplicitConsentText  && (formSettings.explicitConsentText || formSettings.explicitConsentLink)"
          class="sf-consent-row">
        <label class="sf-consent-row__label" for="ExplicitConsentText">
          <input id="ExplicitConsentText"
                 type="checkbox"
                 v-model="explicitConsented"
                 class="sf-consent-row__box"/>
          <span>
            {{ formSettings.explicitConsentText }}
            <a v-if="formSettings.explicitConsentLink"
               :href="formSettings.explicitConsentLink"
               target="_blank"
               class="sf-consent-row__link">
              {{ $t("ExplicitConsentText") }}
            </a>
          </span>
        </label>
      </div>

      <!-- İmza alanları. Danışan her zaman imzalar (onamın kendisi); doktor ve tanık
           formun tanımında açıksa görünür — KVKK gibi onamlarda doktor imzası
           istenmiyor, bazı onamlarda ise hukuken tanık da gerekiyor. -->
      <div class="sf-signature">
        <div class="sf-signature__label">{{ $t("CustomerSignature") }}</div>
        <div class="sf-signature__pad">
          <VueSignaturePad width="350px"
                           height="150px"
                           class="sf-signature__canvas"
                           ref="signaturePad"
                           id="signature"/>
        </div>
      </div>

      <div v-if="showDoctorSignature" class="sf-signature">
        <div class="sf-signature__label">
          {{ $t("DoctorSignature") }}
          <span class="sf-signature__optional">{{ $t("Optional") }}</span>
        </div>
        <div class="sf-signature__pad">
          <VueSignaturePad width="350px"
                           height="150px"
                           class="sf-signature__canvas"
                           ref="doctorSignaturePad"
                           id="doctorSignature"/>
        </div>
      </div>

      <div v-if="showWitnessSignature" class="sf-signature">
        <div class="sf-signature__label">
          {{ $t("WitnessSignature") }}
          <span class="sf-signature__optional">{{ $t("Optional") }}</span>
        </div>
        <div class="sf-signature__name">
          <input v-model="witnessName"
                 type="text"
                 class="form-control"
                 :placeholder="$t('WitnessName')"/>
        </div>
        <div class="sf-signature__pad">
          <VueSignaturePad width="350px"
                           height="150px"
                           class="sf-signature__canvas"
                           ref="witnessSignaturePad"
                           id="witnessSignature"/>
        </div>
      </div>
    </div>

    <!-- Aksiyonlar sabit alt bantta: uzun onam metninde imzalamak için sayfanın
         sonuna kadar kaydırmak gerekmiyor. -->
    <div class="sf-actions">
      <div class="sf-actions__inner">
        <button class="btn btn-secondary btn-sm" type="button" @click="cancel">
          <i class="fa fa-arrow-left"></i> {{ $t("Back") }}
        </button>
        <div class="sf-actions__right">
          <button class="btn btn-secondary btn-sm" type="button" @click="clear">
            <i class="fa fa-eraser"></i> {{ $t("ClearSignature") }}
          </button>
          <button class="btn btn-primary btn-sm"
                  type="button"
                  :disabled="!canSave"
                  @click="save">
            <i class="fa fa-check"></i> {{ $t("Save") }}
          </button>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import SanitizedHtml from "../../../components/shared/SanitizedHtml";
import consentFormService from "../../../services/customerConsentForm";
import managementService from "../../../services/management";
import "../../../plugins/vue-signature-pad";
export default {
  name: "SignForm",
  components: {SanitizedHtml},
  props: {
    customerId: String,
    customerConsentFormId: String,
    clauseConsentType: Number,
  },
  data() {
    return {
      isClausesConsented: 0,
      consentFormPreviewHtml: "",

      // Madde bazlı cevaplar. answers[clauseId] = true/false; cevapsız madde anahtar taşımaz.
      clauses: [],
      answers: {},
      // Açıklama isteyen maddelerin serbest metinleri. details[clauseId] = "..."
      details: {},
      blockerMessage: "",
      isSigned: false,

      // Ek imza alanlari - formun tanimindan gelir (GetCustomerConsentFormClauses).
      showDoctorSignature: false,
      showWitnessSignature: false,
      witnessName: "",

      clarificationConsented: false,
      explicitConsented: false,
      formSettings: {}

    };
  },
  methods: {
    /**
     * Maddenin engelleyici yönü ile verilen cevap çakışıyor mu?
     * blockerAnswerType: 1 = "Evet" engeller, -1 = "Hayır" engeller.
     */
    isBlockingAnswer(clause) {
      const answer = this.answers[clause.clauseId];
      if (answer === undefined || !clause.isBlockerQuestion) return false;

      return (clause.blockerAnswerType === 1 && answer === true)
          || (clause.blockerAnswerType === -1 && answer === false);
    },
    /**
     * Madde, verilen cevapta serbest metin açıklaması istiyor mu?
     * detailAnswerType: 1 = "Evet"te ister, -1 = "Hayır"da ister, null = hiç istemez.
     * Engelleyici maddeden bağımsızdır.
     */
    requiresDetail(clause) {
      const answer = this.answers[clause.clauseId];
      if (answer === undefined) return false;

      return (clause.detailAnswerType === 1 && answer === true)
          || (clause.detailAnswerType === -1 && answer === false);
    },
    /** Gönderilecek açıklama; madde istemiyorsa null (yazılmış metin taşınmaz). */
    detailFor(clause) {
      if (!this.requiresDetail(clause)) return null;

      const value = this.details[clause.clauseId];
      return value ? value.trim() : null;
    },
    setAnswer(clause, value) {
      if (this.isSigned) return;

      // Vue 2: önceden var olmayan anahtar reaktif değildir, $set şart.
      this.$set(this.answers, clause.clauseId, value);
    },
    getClauses() {
      consentFormService
          .getCustomerConsentFormClauses(this.customerConsentFormId)
          .then((data) => {
            if (!data) return;

            // Imza alanlari maddelerden BAGIMSIZ: maddesiz formda da doktor/tanik
            // imzasi istenebiliyor, bu yuzden showClauses kontrolunden once okunur.
            this.showDoctorSignature = data.showDoctorSignature;
            this.showWitnessSignature = data.showWitnessSignature;

            if (!data.showClauses) return;

            this.clauses = data.clauses || [];
            this.blockerMessage = data.blockerMessage || "";
            this.isSigned = data.isSigned;

            // İmzalanmış formda kayıtlı cevaplar geri yüklenir (salt-okunur gösterim).
            // Vue 2: açıklama textarea'sı `details[clauseId]`e v-model bağlanıyor.
            // Anahtar sonradan eklenirse reaktif olmaz, o yüzden her madde için
            // baştan açılır (boş metin `detailFor`da null'a düşer).
            this.clauses.forEach(c => {
              if (c.answer !== null && c.answer !== undefined)
                this.$set(this.answers, c.clauseId, c.answer);

              this.$set(this.details, c.clauseId, c.answerDetail || "");
            });
          });
    },
    getFormUrl() {
      consentFormService
          .getCustomerConsentFormPreviewHtml(this.customerId, this.customerConsentFormId)
          .then((html) => {
            if (!html) {
              this.$toastr.error(this.$t("Error"));
            } else {
              this.consentFormPreviewHtml = html;
            }
          });
    },
    cancel() {
      this.$router.go(-1);
    },
    clear() {
      // Acik olan TUM pad'ler temizlenir; yalniz danisaninkini silmek yaniltici olurdu.
      [this.$refs.signaturePad, this.$refs.doctorSignaturePad, this.$refs.witnessSignaturePad]
          .forEach(pad => { if (pad) pad.clearSignature(); });
    },
    save() {
      const {isEmpty, data} = this.$refs.signaturePad.saveSignature();
      let isValid = this.validateFormSettings();

      // Doktor ve tanik imzasi OPSIYONELDIR: Show*Signature bayraklari "bu sutun formda
      // basilacak mi" demektir, "ekranda imza alinmasi zorunlu" demek degil. Onam ciktisi
      // bu ozellikten once de her zaman uc sutun basiyordu ve doktor/tanik cogu klinikte
      // ciktiyi imzaliyor. Bos kalan sutun eskisi gibi bos basilir (seffaf piksel).
      const doctor = this.showDoctorSignature && this.$refs.doctorSignaturePad
          ? this.$refs.doctorSignaturePad.saveSignature() : null;
      const witness = this.showWitnessSignature && this.$refs.witnessSignaturePad
          ? this.$refs.witnessSignaturePad.saveSignature() : null;

      // Aydınlatma / açık rıza kutuları işaretsizken de sessizce dönülüyordu: alanlar
      // kırmızı çerçeveleniyor ama sayfanın altındaki Kaydet'e basan kullanıcı yukarıdaki
      // çerçeveyi görmüyor.
      if (!isValid) {
        this.$toastr.error(this.$t("ValidationMessage.EmptyAndValidateError"));
        return;
      }

      // Öncesinde `if (!isEmpty)` sessizce düşüyordu: pad boşken Kaydet'e basmak
      // ne istek atıyor ne mesaj veriyordu, kullanıcı ekranın donduğunu sanıyordu.
      if (isEmpty) {
        this.$toastr.error(this.$t("SignatureIsRequired"));
        return;
      }

      let model = {
        signatureImageBase64: data,
        customerConsentFormId: this.customerConsentFormId,
        // Madde bazlı cevaba geçilen formlarda form seviyesi bayrak anlamını yitirdi;
        // yalnız yapısal maddesi olmayan (legacy) formlarda taşınır.
        isClausesConsented: this.clauses.length
            ? 0
            : (this.isClausesConsented ? this.clauseConsentType : 0),
        answers: this.clauses.map(c => ({
          clauseId: c.clauseId,
          answer: this.answers[c.clauseId],
          answerDetail: this.detailFor(c)
        })),
        doctorSignatureImageBase64: doctor && !doctor.isEmpty ? doctor.data : null,
        witnessSignatureImageBase64: witness && !witness.isEmpty ? witness.data : null,
        witnessName: this.showWitnessSignature ? this.witnessName : null,
      };

      consentFormService
          .saveDigitalSignature(model)
          .then((result) => {
            if (result) {
              this.$toastr.success(this.$t("Success"));
              this.$router.go(-1);
            } else {
              this.$toastr.error(this.$t("Error"));
            }
          })
          .catch((error) => {
            // 422: eksik cevap / engelleyici madde. Sunucu da doğruluyor çünkü
            // imza isteği doğrudan API'ye de gönderilebilir.
            const messageKey = error?.response?.data?.messageKey;
            this.$toastr.error(messageKey ? this.$t(messageKey) : this.$t("Error"));
          });
    },
    getFormSettings() {
      managementService
          .getFormSettings()
          .then((data) => {
            this.formSettings = data;
          });
    },
    validateFormSettings() {
      if ((this.formSettings.showClarificationText && !this.clarificationConsented) || (this.formSettings.showExplicitConsentText && !this.explicitConsented)) {

        if (this.formSettings.showClarificationText && !this.clarificationConsented) {

          let clarificationTextCheckbox = document.getElementById("ClarificationText");

          clarificationTextCheckbox.style.outline = "2px solid red";
        }

        if (this.formSettings.showExplicitConsentText && !this.explicitConsented) {

          let explicitConsentTextCheckbox = document.getElementById("ExplicitConsentText");

          explicitConsentTextCheckbox.style.outline = "2px solid red";
        }

        return false;
      } else {


        return true;
      }
    },
  },
  computed: {
    /**
     * Belge, madde bölümünün işaretçilerinden ikiye ayrılır; aradaki STATİK tablo
     * atılır ve yerine etkileşimli Evet/Hayır listesi render edilir. Aksi halde aynı
     * maddeler ekranda iki kez görünüyordu: biri belgenin ortasında tıklanamaz
     * kutucuklarla, biri belgenin altında.
     *
     * İşaretçilerin kaynağı: CustomConsentFormLogic.ClausesSectionStartMarker.
     * Bölünemezse (legacy şablon, maddesiz form, eski API) belge bütün hâliyle
     * basılır — eski davranış bozulmaz.
     */
    clauseSectionBounds() {
      const html = this.consentFormPreviewHtml || "";
      const startMarker = "<!--StellaClausesStart-->";
      const endMarker = "<!--StellaClausesEnd-->";

      const start = html.indexOf(startMarker);
      const end = html.indexOf(endMarker);

      if (start === -1 || end === -1 || end < start) return null;

      return { start: start, end: end + endMarker.length };
    },
    documentBeforeClauses() {
      const bounds = this.clauseSectionBounds;
      if (!bounds || !this.clauses.length) return this.consentFormPreviewHtml;

      return this.consentFormPreviewHtml.slice(0, bounds.start);
    },
    documentAfterClauses() {
      const bounds = this.clauseSectionBounds;
      if (!bounds || !this.clauses.length) return "";

      return this.consentFormPreviewHtml.slice(bounds.end);
    },
    unansweredCount() {
      return this.clauses.filter(c => this.answers[c.clauseId] === undefined).length;
    },
    /** Engelleyen cevabın verildiği ilk madde; yoksa null. */
    blockedClause() {
      return this.clauses.find(c => this.isBlockingAnswer(c)) || null;
    },
    /** Açıklama isteyip de metin alanı boş bırakılan madde sayısı. */
    missingDetailCount() {
      return this.clauses.filter(c => this.requiresDetail(c) && !this.detailFor(c)).length;
    },
    /** Maddesiz (legacy) formda kaydetme her zaman açık; eski davranış korunur. */
    canSave() {
      if (!this.clauses.length) return true;
      return this.unansweredCount === 0 && !this.blockedClause && this.missingDetailCount === 0;
    },
  },
  mounted: function () {
    if (this.customerConsentFormId) {
      this.getFormUrl();
      this.getClauses();
    }
    this.getFormSettings();
  },
};
</script>

<style>
/* Cerceve ID yerine SINIFA baglandi: `#signature` yalnizca danisan pad'ine
   uyuyordu (global override.css'te de `#signature { border: 1px solid #ccc
   !important }` var), doktor ve tanik pad'leri cercevesiz kaliyordu. */
.sf-signature__canvas {
  border: 1px solid #ccc;
  border-radius: 5px;
  background: #fff;
}

/* ── Sayfa düzeni ────────────────────────────────────────────────
   Route layoutless: App.vue çıplak <router-view> basar, sarmalayıcı yok.
   Zemin, ortalama ve alt aksiyon bandı burada kurulur. Tüm kurallar .sf-*
   altında toplanıyor — bu blok scoped DEĞİL, tema sınıflarına sızmamalı. */

.sf-page {
  min-height: 100vh;
  background: #f3f4f6;
  padding: 1.5rem 1rem 6rem;   /* alt: sabit aksiyon bandına yer */
}

/* Kağıt. Ölçüler A4: bu formlar A4 basılmak üzere tasarlanmış ve PDF
   wkhtmltopdf'e 7mm yan boşlukla veriliyor (IConsentFormProvider). Aynı
   ölçüleri kullanmak hem 1900px'e yayılan okunmaz satırları bitiriyor hem de
   ekrandaki görüntüyü basılı/PDF hâliyle aynı hizaya getiriyor — keyfi bir
   piksel genişliği ikisini de ıskalardı. mm birimi bilinçli: sayı PDF ayarıyla
   birebir kalsın. */
.sf-sheet {
  max-width: 210mm;
  width: 100%;
  margin: 0 auto;
  background: #fff;
  border-radius: 6px;
  box-shadow: 0 1px 3px rgba(0, 0, 0, 0.1), 0 1px 2px rgba(0, 0, 0, 0.06);
  padding: 10mm 7mm;
  overflow-x: hidden; /* taşma .sf-document içinde çözülür; kağıt dışına çıkmasın */
}

/* ── Onam metni (backend'den gelen HTML) ──
   BURAYA TİPOGRAFİ/IZGARA KURALI EKLEMEYİN (margin, vertical-align, hücre
   padding'i). Belge kendi düzenini taşıyor; bir denemede bu kurallar başlık
   hizasını ve logoyu bozmuştu.

   Tek istisna: yatay taşmayı kesmek. Bootstrap `.table`, şablondaki
   `.op-cell { white-space:nowrap }` ve Quill/Word'den gelen sabit genişlikli
   tablolar kağıdı yırtıp bu alanda yatay kaydırma çıkarıyor. Logo artık
   hücrede değil (ortalanmış block, 225px), bu yüzden `img{max-width:100%}`
   güvenli. */
.sf-document {
  max-width: 100%;
  overflow-x: auto; /* sığmayan tek parça içerik için son çare */
}

.sf-document table {
  max-width: 100% !important;
  width: 100% !important;
  table-layout: fixed;
}

.sf-document td,
.sf-document th,
.sf-document p,
.sf-document li,
.sf-document span,
.sf-document div {
  overflow-wrap: anywhere;
  word-wrap: break-word;
  white-space: normal !important;
}

.sf-document img {
  max-width: 100%;
  height: auto;
}

/* Legacy onam başlığı: logo | başlık yan yana tabloda geliyor.
   Özel şablon zaten logo üstte / başlık altta; burada legacy'yi aynı düzene çekeriz.
   İmza tabloları (th + img) eşleşmez — yalnız logo + h1..h5 taşıyan tablolar. */
.sf-document table:has(img):has(h1, h2, h3, h4, h5) {
  table-layout: auto !important;
}

.sf-document table:has(img):has(h1, h2, h3, h4, h5) > tbody > tr,
.sf-document table:has(img):has(h1, h2, h3, h4, h5) > tr {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
}

.sf-document table:has(img):has(h1, h2, h3, h4, h5) td {
  display: block;
  width: 100% !important;
  max-width: 100%;
  text-align: center !important;
}

.sf-document table:has(img):has(h1, h2, h3, h4, h5) img {
  display: block;
  width: 225px;
  max-width: 100%;
  margin: 0 auto;
}

.sf-document table:has(img):has(h1, h2, h3, h4, h5) h1,
.sf-document table:has(img):has(h1, h2, h3, h4, h5) h2,
.sf-document table:has(img):has(h1, h2, h3, h4, h5) h3,
.sf-document table:has(img):has(h1, h2, h3, h4, h5) h4,
.sf-document table:has(img):has(h1, h2, h3, h4, h5) h5 {
  margin: 0;
  text-align: center;
}

/* ── Onay satırları (aydınlatma metni, açık rıza, legacy tümüne evet) ── */
.sf-consent-row {
  margin-top: 1rem;
  padding-top: 1rem;
  border-top: 1px solid #eef0f4;
}

/* Etiketin tamamı tıklanabilir, kutucuk ilk satırla hizalı. Önceden metin
   ortalanıyor, kutucuk sola kaçıyordu. */
.sf-consent-row__label {
  display: flex;
  align-items: flex-start;
  gap: 0.6rem;
  margin: 0;
  cursor: pointer;
  font-weight: 400;
  line-height: 1.6;
}

.sf-consent-row__box {
  flex: 0 0 auto;
  width: 18px;
  height: 18px;
  margin-top: 0.2rem;
}

.sf-consent-row__link { text-decoration: underline; }

/* ── İmza ── */
.sf-signature {
  margin-top: 1.75rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eef0f4;
  text-align: center;
}

.sf-signature__optional {
  margin-left: .35rem;
  font-size: .75rem;
  font-weight: 400;
  color: #8a96a3;
}

.sf-signature__label {
  font-weight: 600;
  margin-bottom: 0.75rem;
}

.sf-signature__pad {
  display: flex;
  justify-content: center;
}

/* Tanik adi: imza pad'iyle ayni genislikte ve ustunde. */
.sf-signature__name {
  max-width: 350px;
  margin: 0 auto 0.5rem;
}

/* ── Sabit aksiyon bandı: uzun onam metninde imzalamak için sayfanın
   sonuna kadar kaydırmak gerekmesin. ── */
.sf-actions {
  position: fixed;
  left: 0;
  right: 0;
  bottom: 0;
  background: #fff;
  border-top: 1px solid #e5e7eb;
  box-shadow: 0 -1px 3px rgba(0, 0, 0, 0.06);
  padding: 0.75rem 1rem;
  z-index: 10;
}

.sf-actions__inner {
  max-width: 210mm;   /* kağıtla aynı hizada kalsın */
  margin: 0 auto;
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 0.5rem;
}

.sf-actions__right {
  display: flex;
  gap: 0.5rem;
}

/* Madde bazlı Evet/Hayır. Tablet/kiosk ekranında parmakla kullanılıyor:
   butonlar dokunma hedefi olarak en az 40px yüksekliğinde. */
.sf-clauses {
  margin-top: 1.75rem;
  padding-top: 1.5rem;
  border-top: 1px solid #eef0f4;
}

.sf-clauses__intro {
  font-weight: 600;
  margin-bottom: 0.9rem;
}

.sf-clause {
  padding: 0.6rem 0.9rem;
  border: 1px solid #ebedf2;
  border-radius: 4px;
  margin-bottom: 0.5rem;
}

/* Madde metni + Evet/Hayır aynı satırda; açıklama alanı bu satırın ALTINA
   tam genişlikte açılıyor, o yüzden flex kabuk satıra taşındı. */
.sf-clause__row {
  display: flex;
  align-items: center;
  justify-content: space-between;
  gap: 1rem;
}

.sf-clause--blocked {
  border-color: #dc3545;
  border-width: 2px;
  background-color: #fff5f5;
}

.sf-clause__text {
  flex: 1;
  text-align: left;
  line-height: 1.5;
}

.sf-clause__buttons {
  display: flex;
  gap: 0.5rem;
  flex-shrink: 0;
}

.sf-clause__btn {
  min-width: 84px;
  min-height: 40px;
  border: 1px solid #ebedf2;
  background: #fff;
  font-weight: 600;
}

.sf-clause__btn--no.active {
  background-color: #dc3545;
  border-color: #dc3545;
  color: #fff;
}

.sf-clause__btn--yes.active {
  background-color: #28a745;
  border-color: #28a745;
  color: #fff;
}

.sf-clause__detail {
  margin-top: 0.6rem;
  padding-top: 0.6rem;
  border-top: 1px dashed #ebedf2;
  text-align: left;
}

.sf-clause__detail-label {
  display: block;
  font-weight: 600;
  margin-bottom: 0.35rem;
}

.sf-clause__detail-required {
  color: #dc3545;
}

.sf-clauses__blocker,
.sf-clauses__pending {
  margin-top: 0.75rem;
}

@media (max-width: 768px) {
  .sf-page { padding: 0.75rem 0.5rem 6rem; }

  .sf-sheet { padding: 1.25rem 1rem; }

  .sf-clause__row {
    flex-direction: column;
    align-items: stretch;
    gap: 0.5rem;
  }

  .sf-clause__buttons > .sf-clause__btn {
    flex: 1;
  }
}
</style>